import React, { useRef, useState } from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';

import { CS_WEIGHT, csTokens, glassSurface, secondaryText } from 'components/CallScribe/callScribeTheme';

const KEY_LETTERS: Record<string, string> = {
  1: '',
  2: 'ABC',
  3: 'DEF',
  4: 'GHI',
  5: 'JKL',
  6: 'MNO',
  7: 'PQRS',
  8: 'TUV',
  9: 'WXYZ',
  '*': '',
  0: '+',
  '#': '',
};

const KEY_ORDER: readonly string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const DIAL_ORDER: readonly string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', 'Backspace'];

const DIAL_LABELS: Record<string, string> = {
  '+': '+ Choose a country code',
  Backspace: 'Delete last digit',
};

const COLUMNS = 3;

const KEY_DIAMETER = 64;

const useKeypadStyles = makeStyles((theme: Theme) => ({
  grid: {
    display: 'grid',
    justifyContent: 'center',
  },
  key: {
    borderRadius: '50%',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    transition: 'background 120ms ease, transform 120ms ease',
    '&:hover': {
      background: theme.palette.action.hover,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.text.primary}`,
      outlineOffset: 2,
    },
  },
  keyPressed: {
    background: theme.palette.action.selected,
    transform: 'scale(0.95)',
  },
  keyOnGround: {
    borderRadius: '50%',
    ...glassSurface(theme),
    color: csTokens(theme).onGround,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    transition: 'background 120ms ease, transform 120ms ease',
    '&:hover': {
      background: csTokens(theme).glassStrong,
    },
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).onGround}`,
      outlineOffset: 2,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  keyOnGroundPressed: {
    background: csTokens(theme).onGround,
    color: csTokens(theme).ground,
    transform: 'scale(0.95)',
    '@supports (backdrop-filter: blur(1px))': {
      background: csTokens(theme).onGround,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transform: 'none',
    },
  },
  digit: {
    ...theme.typography.h5,
    fontWeight: 400,
    lineHeight: 1,
  },
  digitCompact: {
    ...theme.typography.h6,
    lineHeight: 1,
  },
  letters: {
    ...theme.typography.caption,
    color: secondaryText(theme),
    letterSpacing: 1,
    marginTop: 2,
  },
  lettersOnGround: {
    ...theme.typography.caption,
    color: csTokens(theme).onGroundMuted,
    letterSpacing: 1,
    marginTop: 2,
  },
  sheetGrid: {
    display: 'grid',
    gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))`,
    gap: 8,
    width: '100%',
  },
  sheetKey: {
    height: 50,
    minWidth: 0,
    borderRadius: 12,
    border: `1px solid ${csTokens(theme).line}`,
    background: csTokens(theme).surface,
    color: csTokens(theme).text,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    transition: 'background-color 120ms ease-out, transform 100ms ease-out',
    '&:hover': { background: csTokens(theme).surface2 },
    '&:active': { background: csTokens(theme).surface3, transform: 'scale(0.97)' },
    '&:focus-visible': { outline: `2px solid ${csTokens(theme).focus}`, outlineOffset: 2 },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'background-color 120ms ease-out',
      '&:active': { transform: 'none' },
    },
  },
  sheetKeyAlt: {
    background: 'transparent',
    borderColor: 'transparent',
    color: csTokens(theme).text2,
    '& svg': { fontSize: 22 },
  },
  sheetDigit: {
    fontSize: 21,
    lineHeight: '22px',
    fontWeight: CS_WEIGHT.strong,
    fontVariantNumeric: 'tabular-nums',
  },
  sheetLetters: {
    fontSize: 9,
    lineHeight: '10px',
    letterSpacing: '0.14em',
    fontWeight: CS_WEIGHT.strong,
    color: csTokens(theme).text2,
    marginTop: 3,
    minHeight: 10,
  },
}));

export type KeypadVariant = 'surface' | 'onGround' | 'sheet';

export type KeypadLayout = 'dtmf' | 'dial';

export interface IKeypadProps {
  onPress: (key: string) => void;
  compact?: boolean;
  variant?: KeypadVariant;
  layout?: KeypadLayout;
}

const ROVING_MOVES: Record<string, (i: number, n: number) => number> = {
  ArrowRight: (i, n) => Math.min(n - 1, i + 1),
  ArrowLeft: i => Math.max(0, i - 1),
  ArrowDown: (i, n) => (i + COLUMNS < n ? i + COLUMNS : i),
  ArrowUp: i => (i - COLUMNS >= 0 ? i - COLUMNS : i),
  Home: () => 0,
  End: (_, n) => n - 1,
};

const DialKeypad: React.FC<{ onPress: (key: string) => void }> = ({ onPress }) => {
  const classes = useKeypadStyles();
  const [focusIndex, setFocusIndex] = useState(0);
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    if (/^[0-9+]$/.test(e.key) || e.key === 'Backspace') {
      e.preventDefault();
      onPress(e.key);
      return;
    }
    const move = ROVING_MOVES[e.key];
    if (!move) return;
    e.preventDefault();
    const next = move(index, DIAL_ORDER.length);
    setFocusIndex(next);
    refs.current[next]?.focus();
  };

  return (
    <div role="group" aria-label="Dial pad" className={classes.sheetGrid}>
      {DIAL_ORDER.map((key, index) => {
        const alt = key === '+' || key === 'Backspace';
        return (
          <button
            key={key}
            ref={node => { refs.current[index] = node; }}
            type="button"
            tabIndex={index === focusIndex ? 0 : -1}
            aria-label={DIAL_LABELS[key] ?? `Dial ${key}`}
            onFocus={() => { setFocusIndex(index); }}
            onKeyDown={e => { onKeyDown(e, index); }}
            onClick={() => { onPress(key); }}
            className={`${classes.sheetKey} ${alt ? classes.sheetKeyAlt : ''}`}
          >
            {key === 'Backspace' ? (
              <BackspaceOutlinedIcon aria-hidden />
            ) : (
              <>
                <span className={classes.sheetDigit}>{key}</span>
                {alt ? null : (
                  <span className={classes.sheetLetters}>{key === '0' ? '' : KEY_LETTERS[key]}</span>
                )}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};

const Keypad: React.FC<IKeypadProps> = ({ onPress, compact, variant = 'surface', layout = 'dtmf' }) => {
  const classes = useKeypadStyles();
  const [pressedKey, setPressedKey] = useState<string | undefined>(undefined);

  if (layout === 'dial' || variant === 'sheet') {
    return <DialKeypad onPress={onPress} />;
  }

  const diameter = compact ? Math.round(KEY_DIAMETER * 0.85) : KEY_DIAMETER;
  const gap = compact ? 10 : 14;
  const onGround = variant === 'onGround';
  const keyClass = onGround ? classes.keyOnGround : classes.key;
  const pressedClass = onGround ? classes.keyOnGroundPressed : classes.keyPressed;
  const lettersClass = onGround ? classes.lettersOnGround : classes.letters;

  return (
    <div
      role="group"
      aria-label="Dial pad"
      className={classes.grid}
      style={{ gridTemplateColumns: `repeat(3, ${diameter}px)`, gap }}
    >
      {KEY_ORDER.map(key => {
        const isPressed = pressedKey === key;
        return (
          <button
            key={key}
            type="button"
            aria-label={`Dial ${key}`}
            onPointerDown={() => { setPressedKey(key); }}
            onPointerUp={() => { setPressedKey(undefined); }}
            onPointerLeave={() => { setPressedKey(undefined); }}
            onClick={() => { onPress(key); }}
            className={`${keyClass} ${isPressed ? pressedClass : ''}`}
            style={{ width: diameter, height: diameter }}
          >
            <span className={`${classes.digit} ${compact ? classes.digitCompact : ''}`}>
              {key}
            </span>
            {KEY_LETTERS[key] ? (
              <span className={lettersClass}>{KEY_LETTERS[key]}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default Keypad;
